import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import UserManagement from '../components/UserManagement';
import PromptManagement from '../components/PromptManagement';
import UsageTracking from '../components/UsageTracking';
import api from 'services/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production' 
    ? '/api' 
    : 'http://localhost:5000/api'
);

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const history = useHistory();
  const [bugReportsCount, setBugReportsCount] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    if (user?.role !== 'admin') {
      return () => {
        cancelled = true;
      };
    }

    const fetchBugReportsCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await api.get(`${API_BASE_URL}/bug-reports`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (cancelled) return;
        setBugReportsCount(response.data.length);
      } catch (err) {
        console.error('Failed to fetch bug reports count:', err);
      }
    };

    fetchBugReportsCount();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageHeader />
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{t('navigation.dashboard')}</h2>
          <p className="text-gray-600 dark:text-gray-400">{t('dashboard.overview')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-8">
            <UserManagement />
          </div>
          <div className="space-y-8">
            <UsageTracking />
            <PromptManagement />
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold mb-4 flex items-center text-gray-900 dark:text-white">
                <span className="mr-2">🐛</span>
                Bug Reports
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                View and manage all bug reports submitted by users.
              </p>
              {bugReportsCount > 0 && (
                <p className="text-red-600 dark:text-red-400 font-semibold mb-4">
                  ⚠️ {bugReportsCount} unresolved bug report{bugReportsCount !== 1 ? 's' : ''}
                </p>
              )}
              <button
                onClick={() => history.push('/bug-reports')}
                className="btn-primary w-full"
              >
                View Bug Reports
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;